import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import {
  useGetActivityFields,
  useGetActivityQuickEditFields,
  useGetUsedPaths,
  useUpdateActivityDrafts,
} from "../../hooks/useActivities";
import {
  Activity,
  UpdateActivitiesMetadata,
  UpdateActivitiesRequest,
} from "../../models/Activity";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { FieldValue } from "../../models/Field";
import { ReportType } from "../../services/ReportService";
import { getActivityColumnsFromColumnOrder } from "../../services/ViewService";
import {
  PaginatedList,
  PaginatedListEditingOptions,
} from "../list/PaginatedList";

export const DraftActivityList = () => {
  const { t } = useTranslation();
  const { mutateAsync: updateActivities, isPending: updateActivitiesPending } =
    useUpdateActivityDrafts();

  const { data: usedPaths } = useGetUsedPaths();

  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetActivityFields();
  const { data: quickEditViewDefinition } = useGetActivityQuickEditFields();

  const columns = useMemo<VsaTableColumnDefinition<Activity>[]>(() => {
    if (viewDefinitionLoading || !viewDefinition) {
      return [];
    }

    return getActivityColumnsFromColumnOrder(
      viewDefinition.standardFields,
      viewDefinition.fields,
      viewDefinition.draftsFieldOrder,
      true
    );
  }, [viewDefinition, viewDefinitionLoading]);

  const mapActivityToRequest = (
    activity: Activity
  ): UpdateActivitiesMetadata => {
    if (!viewDefinition?.fields) {
      throw Error("Activity fields are undefined.");
    }

    const newFieldsWithValues: Record<string, FieldValue> = {};

    if (activity.fieldValues) {
      viewDefinition.fields.forEach((field) => {
        if (field.type === 0) {
          const tagFieldValue = activity.fieldValues?.tags[field.uniqueName];

          if (tagFieldValue) {
            newFieldsWithValues[field.uniqueName] = tagFieldValue.map(
              (tag) => tag.id
            );
          }
        } else {
          const booleanFieldValue =
            activity.fieldValues?.booleans[field.uniqueName];

          if (booleanFieldValue !== undefined && booleanFieldValue !== null) {
            newFieldsWithValues[field.uniqueName] = booleanFieldValue;
          }
        }
      });
    }

    return {
      versionId: activity.versionId,
      title: activity.title,
      fieldsWithValues: newFieldsWithValues,
    };
  };

  const onClickSave = async (activitiesToSave: Activity[]) => {
    const request: UpdateActivitiesRequest = {
      activityMetadata: activitiesToSave.map(mapActivityToRequest),
    };

    await updateActivities(request);
  };

  const editOptions: PaginatedListEditingOptions<Activity> = {
    usedPaths: usedPaths,
    createFormRoute: ClientRoutes.ActivityCreate,
    allowQuickEdit: true,
    onSave: onClickSave,
    savePending: updateActivitiesPending,
    viewDefinition: quickEditViewDefinition,
    entityType: "activity",
  };

  return (
    <PaginatedList<Activity>
      apiEndpoint={ApiEndpoints.ActivityDrafts}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchActivity")}
      cacheKey={CacheKeys.Activity.ActivityDraftList}
      filtersEndpoint={ApiEndpoints.ActivityFilters}
      editingOptions={editOptions}
      reportType={ReportType.DraftActivities}
      storageKey="activities:drafts"
      libraryFilterScope={{ listType: "activity" }}
      selectionPersistenceKey={SelectionPersistenceKeys.ActivitiesDrafts}
    />
  );
};
